import type * as THREE from 'three'
import type { GraphicsBackendKind } from '@/graphics/GraphicsConfig'
import {
  createMeshClipMaterialVariant,
  type MeshClipMaterialVariant,
  type MeshClipUniformBinding,
} from '@/plugins/mesh/clipping/MeshClipShader'
import type { MeshAppearance, MeshHandle, MeshPrimitive } from '@/plugins/mesh/types'
import { createMeshAppearanceMaterial } from '@/shared/materials/createMeshAppearanceMaterial'

interface PrimitiveAppearanceState {
  readonly primitive: MeshPrimitive
  derivedMaterial?: THREE.Material
  clipVariant?: MeshClipMaterialVariant
}

interface MeshAppearanceControllerOptions {
  readonly clipBinding?: MeshClipUniformBinding
  readonly clipBackend?: GraphicsBackendKind
  readonly onUnsupportedClipMaterial?: (materialType: string) => void
}

function createDerivedMaterial(
  primitive: MeshPrimitive,
  appearance: MeshAppearance
): THREE.Material {
  return createMeshAppearanceMaterial(primitive.geometry, primitive.material, appearance)
}

/** 管理单个 Mesh 句柄的派生外观，并保持 Loader 原始资源所有权不变。 */
export class MeshAppearanceController {
  private readonly states: PrimitiveAppearanceState[]
  private readonly clipBinding?: MeshClipUniformBinding
  private readonly clipBackend: GraphicsBackendKind
  private readonly onUnsupportedClipMaterial: (materialType: string) => void
  private clippingEnabled = false
  private disposed = false
  private appearance?: MeshAppearance

  constructor(handle: MeshHandle, options: MeshAppearanceControllerOptions = {}) {
    this.states = handle.primitives.map((primitive) => ({ primitive }))
    this.clipBinding = options.clipBinding
    this.clipBackend = options.clipBackend ?? 'webgl2'
    this.onUnsupportedClipMaterial = options.onUnsupportedClipMaterial ?? (() => {})
  }

  /**
   * 将节点级外观应用到句柄的全部 Primitive。
   * @param appearance 要应用的运行时外观。
   */
  apply(appearance: MeshAppearance): void {
    if (this.disposed) return
    this.appearance = { ...appearance }
    for (const state of this.states) {
      this.disposeClipVariant(state)
      state.derivedMaterial?.dispose()
      state.derivedMaterial = createDerivedMaterial(state.primitive, appearance)
      this.syncMaterial(state)
    }
  }

  /** 将加载中追加的 Primitive 纳入当前外观和裁切状态。 */
  append(primitives: readonly MeshPrimitive[]): void {
    if (this.disposed) return
    for (const primitive of primitives) {
      const state: PrimitiveAppearanceState = { primitive }
      if (this.appearance) state.derivedMaterial = createDerivedMaterial(primitive, this.appearance)
      this.states.push(state)
      this.syncMaterial(state)
    }
  }

  /** 完整结果交接时释放预览派生材质，并沿用当前外观。 */
  replace(primitives: readonly MeshPrimitive[]): void {
    if (this.disposed) return
    for (const state of this.states) {
      this.disposeClipVariant(state)
      state.primitive.object.material = state.primitive.material
      state.derivedMaterial?.dispose()
    }
    this.states.length = 0
    this.append(primitives)
  }

  /** 启用或停用裁切材质变体，并保持当前外观不变。 */
  setClippingEnabled(enabled: boolean): void {
    if (this.disposed || this.clippingEnabled === enabled) return
    this.clippingEnabled = enabled
    for (const state of this.states) {
      this.disposeClipVariant(state)
      this.syncMaterial(state)
    }
  }

  /** 当前对象材质是否能够与 CPU 拾取使用相同裁切语义。 */
  isClipSupported(object: THREE.Object3D): boolean {
    if (!this.clippingEnabled) return false
    const state = this.states.find((candidate) => candidate.primitive.object === object)
    return state?.clipVariant?.isSupported() ?? false
  }

  /** 恢复 Loader 原材质并释放全部派生材质。 */
  dispose(): void {
    if (this.disposed) return
    this.disposed = true
    for (const state of this.states) {
      this.disposeClipVariant(state)
      state.primitive.object.material = state.primitive.material
      state.derivedMaterial?.dispose()
      state.derivedMaterial = undefined
    }
  }

  private syncMaterial(state: PrimitiveAppearanceState): void {
    const baseMaterial = state.derivedMaterial ?? state.primitive.material
    if (!this.clippingEnabled || !this.clipBinding) {
      state.primitive.object.material = baseMaterial
      return
    }

    state.clipVariant =
      createMeshClipMaterialVariant(
        baseMaterial,
        this.clipBinding,
        this.onUnsupportedClipMaterial,
        this.clipBackend
      ) ?? undefined
    state.primitive.object.material = state.clipVariant?.material ?? baseMaterial
  }

  private disposeClipVariant(state: PrimitiveAppearanceState): void {
    if (!state.clipVariant) return
    state.clipVariant.material.dispose()
    state.clipVariant = undefined
  }
}
